'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  role: string;
  company: string;
}

const mockTestimonials: Testimonial[] = [
  {
    id: 1,
    quote: 'Valinaire completely transformed our social media presence. The content finally feels consistent and on-brand, and we\'ve noticed a real increase in engagement from our local community.',
    author: 'Emily Carter',
    role: 'Marketing Manager',
    company: 'Online Retail Brand',
  },
  {
    id: 2,
    quote: 'Working with Valinaire has been incredibly easy. They understand what small businesses need and handle everything from posting to strategy so we can focus on running our business.',
    author: 'Edy Alvarez',
    role: 'Co-Founder',
    company: 'Chapel Hill Taqueria',
  },
  {
    id: 3,
    quote: 'What stood out about Valinaire was the attention to detail and clear strategy. Our social media finally feels intentional instead of random, and the results speak for themselves.',
    author: 'Lucas Linares',
    role: 'General Manager',
    company: 'Raleigh Café & Bar',
  },
];

interface TestimonialsSectionProps {
  isHydrated: boolean;
}

const TestimonialsSection = ({ isHydrated }: TestimonialsSectionProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? mockTestimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === mockTestimonials.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      handleNext();
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, currentIndex]);

  const currentTestimonial = mockTestimonials[currentIndex];

  return (
    <section className="py-20 lg:py-32 bg-secondary">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-24">
          <h2 className="font-headline font-bold text-4xl lg:text-6xl text-primary tracking-tight mb-6">
            Client Testimonials
          </h2>
          <div className="w-24 h-[2px] bg-primary mx-auto" />
        </div>

        {/* Testimonial Content */}
        <div className="relative">
          <div className="text-center space-y-8">
            {/* Quote */}
            <blockquote
              className="font-body text-xl lg:text-3xl text-primary leading-relaxed tracking-wide"
              style={{
                opacity: isHydrated ? 1 : 0,
                transition: 'opacity 0.6s ease-out',
              }}
            >
              &quot;{currentTestimonial.quote}&quot;
            </blockquote>

            {/* Author Info */}
            <div
              className="space-y-2"
              style={{
                opacity: isHydrated ? 1 : 0,
                transition: 'opacity 0.6s ease-out 0.2s',
              }}
            >
              <p className="font-headline font-bold text-lg lg:text-xl text-primary">
                {currentTestimonial.author}
              </p>
              <p className="font-body text-sm lg:text-base text-text-secondary">
                {currentTestimonial.role}, {currentTestimonial.company}
              </p>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-4 mt-12">
            <button
              onClick={handlePrevious}
              className="w-12 h-12 flex items-center justify-center border border-border text-primary transition-all duration-fast hover:bg-primary hover:text-primary-foreground hover:-translate-y-0.5"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeftIcon" size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center gap-2">
              {mockTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 transition-all duration-fast ${
                    index === currentIndex
                      ? 'bg-primary w-8' :'bg-border hover:bg-muted-foreground'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="w-12 h-12 flex items-center justify-center border border-border text-primary transition-all duration-fast hover:bg-primary hover:text-primary-foreground hover:-translate-y-0.5"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRightIcon" size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;